<script lang="ts" setup>
import type { CreateFilterTemplate, OpenerType } from '~/types'

const props = defineProps<{ opener: OpenerType['openFilterTemplatePopup'] }>()

const { $trpc, queryClient, useMutation, makeTrpcErrorToast, makeTrpcSuccessToast } = useMutationHelpers()

const { data: popupData, close: closePopupRaw } = props.opener
const formRef = useTemplateRef('formRef')
const {
  showUnsavedChangesModal,
  attemptClose,
  forceClose,
  cancelDiscard,
} = usePopupGuard(
  computed(() => formRef.value?.isDirty ?? false),
  closePopupRaw,
)

const payload = computed((): CreateFilterTemplate | undefined => {
  if (popupData.value?.mode === 'create') {
    return {
      mode: 'create',
      data: {
        name: '',
        group: popupData.value?.data.group,
        base64: popupData.value?.data.base64,
      },
    }
  }
})

const { $i18n } = useNuxtApp()
const create = useMutation({
  mutationFn: $trpc.filterTemplates.createFilterTemplate.mutate,
  onError: makeTrpcErrorToast({ entity: $i18n.t('filters.template.entity'), mode: 'create' }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['filterTemplates', 'getAll'] })
    makeTrpcSuccessToast({ entity: $i18n.t('filters.template.entity'), mode: 'create' })
    forceClose()
  },
})

const saveCreateOrUpdateForm = (payload: CreateFilterTemplate) => {
  if (payload.mode === 'create') {
    create.mutate(payload.data)
  }
}
</script>

<template>
  <ThePopup
    v-if="payload && popupData"
    :show="Boolean(payload)"
    :title="$t(`filters.template.page.${payload?.mode ?? 'create'}`)"
    @close="attemptClose"
  >
    <FiltersTemplateCreateOrUpdateForm
      v-if="payload"
      ref="formRef"
      :payload="payload"
      :is-loading="create.isPending.value"
      @save="saveCreateOrUpdateForm"
      @cancel="attemptClose"
    />
  </ThePopup>

  <TheConfirmPopup
    :show="showUnsavedChangesModal"
    :title="$t('popup.guard.title')"
    :text="$t('popup.guard.message')"
    :confirm-text="$t('popup.guard.discard')"
    :cancel-text="$t('popup.guard.cancel')"
    @close="cancelDiscard"
    @confirm="forceClose"
  />
</template>
